import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import HotmailDeliveryNotice from './HotmailDeliveryNotice';

// Exit-intent modal: fires on real exit gestures, shows a server-decided variant
// (consistent A/B), respects per-session cooldown via localStorage. Suppressed
// on /checkout, /admin, and when cart is empty.

const COOLDOWN_KEY = 'sg_eim_dismissed_at';
const SHOWN_THIS_SESSION_KEY = 'sg_eim_shown_this_session';

export default function ExitIntentModal() {
  const [variant, setVariant] = useState(null);
  const [open, setOpen] = useState(false);
  const [emailVal, setEmailVal] = useState('');
  const armedRef = useRef(true);
  const cart = useCart?.() || {};

  const cartItems = cart.items || cart.cart || [];
  const cartTotalCents = Math.round(
    (cart.total || cart.subtotal ||
     (cartItems.reduce ? cartItems.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0) : 0)) * 100
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path.startsWith('/checkout') || path.startsWith('/admin')) return;
    if (sessionStorage.getItem(SHOWN_THIS_SESSION_KEY) === '1') return;
    if (cartTotalCents < 1000) return;

    const dismissedAt = Number(localStorage.getItem(COOLDOWN_KEY) || 0);
    const cooldownMs = 24 * 60 * 60 * 1000;
    if (dismissedAt && Date.now() - dismissedAt < cooldownMs) return;

    const trigger = async () => {
      if (!armedRef.current) return;
      armedRef.current = false;
      try {
        const r = await fetch('/api/exit-intent/trigger', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cart_total_cents: cartTotalCents,
            cart_item_count: cartItems.length,
            session_id: getSessionId(),
          }),
        });
        const j = await r.json();
        if (j.variant) {
          setVariant(j.variant);
          setOpen(true);
          sessionStorage.setItem(SHOWN_THIS_SESSION_KEY, '1');
        }
      } catch (_) {}
    };

    // Desktop: cursor leaves through top edge
    const onMouseLeave = (e) => {
      if (e.clientY <= 0 && e.relatedTarget == null) trigger();
    };
    // Mobile: tab/visibility blur OR back-button intent (history pop with cart)
    const onVisibility = () => { if (document.hidden) trigger(); };
    // Mobile fallback: 60s of inactivity while cart has stuff
    const inactivityTimer = setTimeout(trigger, 60_000);

    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(inactivityTimer);
    };
  }, [cartTotalCents, cartItems.length]);

  const close = (outcome = 'dismissed') => {
    setOpen(false);
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    if (variant?.key) {
      fetch('/api/exit-intent/outcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variant_key: variant.key, outcome, session_id: getSessionId() }),
      }).catch(() => {});
    }
  };

  const onPrimary = () => {
    if (!variant) return close();
    if (variant.cta_action === 'apply_coupon' && variant.cta_payload) {
      try {
        sessionStorage.setItem('sg_pending_coupon', variant.cta_payload);
        navigator.clipboard?.writeText(variant.cta_payload).catch(() => {});
      } catch (_) {}
      close('converted');
      window.location.href = '/cart';
    } else if (variant.cta_action === 'capture_email') {
      if (!/^[^@]+@[^@]+\.[^@]+$/.test(emailVal)) return;
      // Reuse newsletter signup endpoint if available; otherwise just log outcome
      fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailVal, source: 'exit-intent-cart' }),
      }).catch(() => {});
      close('email_captured');
      alert('Saved! Check your email — we sent you a link to your cart.');
    } else {
      close('converted');
    }
  };

  if (!open || !variant) return null;
  return (
    <div role="dialog" aria-label={variant.headline} style={overlay} onClick={() => close()}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <button onClick={() => close()} style={closeBtn} aria-label="Dismiss">×</button>
        <div style={{ fontSize: 13, color: '#3b4cca', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>Hold up</div>
        <h2 style={{ margin: '8px 0 12px', fontSize: 24 }}>{variant.headline}</h2>
        <p style={{ color: '#475569', lineHeight: 1.55 }}>{variant.body}</p>
        {variant.cta_action === 'capture_email' ? (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="email" placeholder="your@email.com" value={emailVal}
                     onChange={e => setEmailVal(e.target.value)}
                     style={{ flex: '1 1 180px', minWidth: 0, padding: 12, borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 16 }} />
              <button onClick={onPrimary} style={primaryBtn}>{variant.cta_label}</button>
            </div>
            <HotmailDeliveryNotice email={emailVal} />
          </div>
        ) : (
          <button onClick={onPrimary} style={{ ...primaryBtn, marginTop: 16, width: '100%' }}>{variant.cta_label}</button>
        )}
        <button onClick={() => close()} style={ghostBtn}>No thanks, continue browsing</button>
      </div>
    </div>
  );
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem('sg_session_id');
    if (!id) { id = Math.random().toString(36).slice(2, 10) + Date.now().toString(36); sessionStorage.setItem('sg_session_id', id); }
    return id;
  } catch (_) { return null; }
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const card = { background: '#fff', borderRadius: 12, padding: 'clamp(20px, 5vw, 32px)', maxWidth: 460, width: '100%', boxShadow: '0 24px 64px rgba(0,0,0,0.4)', position: 'relative' };
const closeBtn = { position: 'absolute', top: 4, right: 4, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: '#94a3b8' };
const primaryBtn = { background: '#3b4cca', color: '#fff', border: 0, borderRadius: 8, padding: '12px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 };
const ghostBtn = { background: 'transparent', border: 0, color: '#64748b', cursor: 'pointer', marginTop: 12, fontSize: 13, textDecoration: 'underline', display: 'block', textAlign: 'center', width: '100%' };
